/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CdkScrollable } from '@angular/cdk/scrolling';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { BehaviorSubject, ReplaySubject, Subject, Subscription, takeUntil } from 'rxjs';
import { PropertyService } from '../../services';
import { resolveUngroupedHinKeys, resolveUngroupedVinKeys } from '../decode-support';
import {
  buildHinPatch,
  HinDecoder,
  MarineVesselFieldKeys,
  normalizeHin,
} from '../marine-vessel-property-input/hin-decode';
import { createPropertyControl } from '../properties/abstract-property-input.component';
import { buildVinPatch, decodeVin, VehicleFieldKeys } from '../vehicle-property-input/vin-decode';

export interface PropertySetInputValue {
  propertyValues: Record<string, unknown>;
  dimensionValues: Record<string, unknown>;
}

export type OrderedSetAssignment =
  | {
      kind: 'PROPERTY';
      order: number;
      propertyAssignment: PropertySet.PropertyAssignment;
    }
  | {
      kind: 'DIMENSION';
      order: number;
      dimensionAssignment: PropertySet.DimensionAssignment;
    };

export function getOrderedAssignments(set: PropertySet.Model): OrderedSetAssignment[] {
  return [
    ...(set.properties ?? []).map<OrderedSetAssignment>(propertyAssignment => ({
      kind: 'PROPERTY',
      order: propertyAssignment.order,
      propertyAssignment,
    })),
    ...(set.dimensions ?? []).map<OrderedSetAssignment>(dimensionAssignment => ({
      kind: 'DIMENSION',
      order: dimensionAssignment.order,
      dimensionAssignment,
    })),
  ].sort((left, right) => left.order - right.order);
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-property-set-input',
  templateUrl: './property-set-input.component.html',
  standalone: false,
  hostDirectives: [CdkScrollable],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PropertySetInputComponent,
    },
    { provide: NG_VALIDATORS, useExisting: PropertySetInputComponent, multi: true },
  ],
})
export class PropertySetInputComponent implements ControlValueAccessor, OnInit, OnDestroy, Validator {
  constructor(
    private propertySvc: PropertyService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private toastSvc: ToastService
  ) {}

  @PropertyListener('setId') setId$ = new BehaviorSubject<string | null>(null);
  @Input() setId!: string;

  @Input() showHidden = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() extraData?: Record<string, any>;

  form!: FormGroup;

  get valid() {
    return this.form?.valid ?? false;
  }

  private _value: PropertySetInputValue = { propertyValues: {}, dimensionValues: {} };
  get value() {
    return this._value;
  }
  @Input() set value(value: PropertySetInputValue | Record<string, unknown>) {
    this._value = this._normalizeValue(value);
    this.patchFormValue();
  }

  @Input() disabled = false;

  set!: PropertySet.Model;
  orderedAssignments: OrderedSetAssignment[] = [];

  /** Top-level vehicle fields the VIN decode fills in; empty unless the set has a VIN alongside other vehicle details. */
  vinDecodeKeys: VehicleFieldKeys = {};
  decodingVin = false;

  /** Top-level vessel fields the HIN decode fills in; empty unless the set has a HIN alongside other vessel details. */
  hinDecodeKeys: MarineVesselFieldKeys = {};
  private hinDecoder = new HinDecoder(this.toastSvc);

  protected onDestroy$ = new Subject<void>();
  protected touched$ = new ReplaySubject<void>(1);
  private formValueChangesSub?: Subscription;
  private hinValueChangesSub?: Subscription;

  onChange = (_value: PropertySetInputValue) => {};
  onTouched = () => {};

  validate(): ValidationErrors | null {
    if (this.form) return extractErrors(this.form);
    return null;
  }

  writeValue(value: PropertySetInputValue | Record<string, unknown> | null): void {
    this._value = this._normalizeValue(value ?? {});
    this.patchFormValue();
  }

  registerOnChange(fn: (value: PropertySetInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
    this.touched$.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.onTouched());
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async ngOnInit() {
    this.setId$.pipe(takeUntil(this.onDestroy$)).subscribe(setId => {
      void this.loadPropertySet(setId);
    });
  }

  ngOnDestroy() {
    this.formValueChangesSub?.unsubscribe();
    this.hinValueChangesSub?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.touched$.complete();
  }

  get propertyValuesForm(): FormGroup {
    return this.form.get('propertyValues') as FormGroup;
  }

  get dimensionValuesForm(): FormGroup {
    return this.form.get('dimensionValues') as FormGroup;
  }

  private async loadPropertySet(setId: string | null) {
    if (!setId) {
      this.set = undefined as never;
      this.form = undefined as never;
      this.orderedAssignments = [];
      this.vinDecodeKeys = {};
      this.hinDecodeKeys = {};
      this.formValueChangesSub?.unsubscribe();
      this.hinValueChangesSub?.unsubscribe();
      this.detectChangesSafe();
      return;
    }

    this.set = await this.propertySvc.retrievePropertySet(setId);

    this.set.properties = this.set.properties?.sort((a, b) => a.order - b.order);
    this.set.dimensions = this.set.dimensions?.sort((a, b) => a.order - b.order);
    this.orderedAssignments = getOrderedAssignments(this.set);
    // Grouped vehicle/vessel fields decode inside their own forms; these cover the ones placed directly in the set.
    this.vinDecodeKeys = resolveUngroupedVinKeys(this.set.properties ?? []);
    this.hinDecodeKeys = resolveUngroupedHinKeys(this.set.properties ?? []);

    const propertyControls: Record<string, FormControl> = {};
    for (const assignment of this.set.properties ?? []) {
      const property = assignment.property!;
      propertyControls[property.valueKey] = createPropertyControl(property, assignment.required);
    }

    const dimensionControls: Record<string, FormControl> = {};
    for (const assignment of this.set.dimensions ?? []) {
      if (assignment.dimension) {
        dimensionControls[assignment.dimensionId] = new FormControl<string | number | null>(
          null,
          assignment.required ? [Validators.required] : []
        );
      }
    }

    this.form = new FormGroup({
      propertyValues: new FormGroup(propertyControls),
      dimensionValues: new FormGroup(dimensionControls),
    });

    this.patchFormValue();
    this.formValueChangesSub?.unsubscribe();
    this.formValueChangesSub = this.form.valueChanges.subscribe(value => this.onChange(value));

    this.hinValueChangesSub?.unsubscribe();
    const hinKey = this.hinDecodeKeys.hin;
    if (hinKey) {
      this.hinValueChangesSub = this.propertyValuesForm
        .get(hinKey)
        ?.valueChanges.subscribe(rawHin => void this.decodeHin(String(rawHin ?? '')));
    }

    if (this.disabled) this.form.disable();
    else this.form.enable();

    this.detectChangesSafe();
  }

  isVinDecodeField(assignment: PropertySet.PropertyAssignment): boolean {
    return !!this.vinDecodeKeys.vin && assignment.property?.valueKey === this.vinDecodeKeys.vin;
  }

  get vinDecodeValue(): string {
    const key = this.vinDecodeKeys.vin;
    const value = key ? this.propertyValuesForm?.get(key)?.value : null;
    return typeof value === 'string' ? value.trim() : '';
  }

  async useVIN() {
    const vin = this.vinDecodeValue;
    if (!vin || this.decodingVin) return;

    this.decodingVin = true;
    try {
      const results = await decodeVin(this.http, this.toastSvc, vin);
      // The VIN was edited while the lookup ran; those results belong to the old value.
      if (results && this.vinDecodeValue === vin) {
        this.propertyValuesForm.patchValue(buildVinPatch(this.vinDecodeKeys, results, vin));
      }
    } finally {
      this.decodingVin = false;
      this.detectChangesSafe();
    }
  }

  private async decodeHin(rawHin: string) {
    const result = await this.hinDecoder.decode(rawHin);
    if (!result || !this.form) return;

    this.propertyValuesForm.patchValue(buildHinPatch(this.hinDecodeKeys, result.decoded, result.hin));
    this.detectChangesSafe();
  }

  /** Values written in from outside are saved data, so their HIN counts as decoded and won't overwrite its fields. */
  private patchFormValue() {
    if (!this.form) return;

    const hinKey = this.hinDecodeKeys.hin;
    if (hinKey) {
      const hin = normalizeHin(String(this._value.propertyValues[hinKey] ?? ''));
      this.hinDecoder.lastDecodedHin = hin || null;
    }

    this.form.patchValue(this._value);
  }

  /** The set resolves after an await, so run CD or the fields never paint. */
  private detectChangesSafe() {
    try {
      this.cdr.detectChanges();
    } catch {
      // View may already be destroyed if the host closed while the set was loading.
    }
  }

  private _normalizeValue(value: PropertySetInputValue | Record<string, unknown> | null): PropertySetInputValue {
    if (!value) return { propertyValues: {}, dimensionValues: {} };
    if ('propertyValues' in value || 'dimensionValues' in value) {
      return {
        propertyValues: ((value as PropertySetInputValue).propertyValues as Record<string, unknown>) ?? {},
        dimensionValues: ((value as PropertySetInputValue).dimensionValues as Record<string, unknown>) ?? {},
      };
    }
    // Legacy flat format — treat all keys as property values
    return { propertyValues: value as Record<string, unknown>, dimensionValues: {} };
  }
}
